// v2.220.04: recuperação defensiva de quota do localStorage no app nativo/iOS.
// O IndexedDB é a fonte principal das coleções grandes. localStorage deve priorizar
// identidade/sessão e chaves pequenas de boot, evitando que uma quota cheia impeça login.

const PRESERVED_LOCAL_STORAGE_KEYS = new Set([
  "fz:session",
  "fz:activeFarm",
  "fz:device_id",
  "fz:server_install_id",
  "fz:server_install_ack",
  "fz:server_install_last_check",
  "fz:server_install_notice",
  "fz:appVersion",
  "fz:appVersion:server",
  "fz:update:attempted",
]);

function normalizeKey(key) {
  return String(key || "");
}

export function isQuotaExceededError(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" || /quota/i.test(message);
}

export function shouldPreserveLocalStorageKey(key) {
  const k = normalizeKey(key);
  return PRESERVED_LOCAL_STORAGE_KEYS.has(k) || k.startsWith("auth:admin:");
}

export function isLargeOrRebuildableLocalStorageKey(key) {
  const k = normalizeKey(key);
  if (!k) return false;
  if (shouldPreserveLocalStorageKey(k)) return false;

  // Coleções por fazenda e sombras de sync já são persistidas no IndexedDB.
  if (/^fz:farm_[^:]+:/.test(k)) return true;
  if (/^fz:[^:]+:(orders|notifications|services|occurrences|thirdPartyServices|stockMovements|stock|inventory|purchases|vet|veterinary|inspections|machinery|locations|people|employees)(:|$)/i.test(k)) return true;
  if (/:_shadow$/.test(k) || /:_ops$/.test(k) || /:_pending$/.test(k) || /:_cursor$/.test(k) || /:_delta_cursor$/.test(k) || /:_ver$/.test(k)) return true;

  // Logs, telemetria e filas podem ser reconstruídos ou rebaixados sem deslogar usuário.
  if (k === "syncQueue" || k === "fz:conflict_log") return true;
  if (k.startsWith("fz:sync") || k.startsWith("fz:storage-warning:")) return true;
  if (k.startsWith("fz:bootTelemetry") || k.startsWith("fz:climateTelemetry")) return true;
  if (k.startsWith("fz:liveActivity:") || k.startsWith("fz:liveActivities:")) return true;
  if (k.startsWith("fz:admin:open-") || k.startsWith("fz:open-")) return true;
  if (k.startsWith("fz:moduleCardsConfig") || k.startsWith("fz:stockCardsConfig")) return false;
  return false;
}

export function recoverLocalStorageQuota(reason = "quota") {
  if (typeof window === "undefined" || !window.localStorage) return 0;
  let removed = 0;
  try {
    const keys = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key) keys.push(key);
    }

    // Remover primeiro entradas grandes/reconstruíveis.
    keys.forEach((key) => {
      if (!isLargeOrRebuildableLocalStorageKey(key)) return;
      try {
        window.localStorage.removeItem(key);
        removed += 1;
      } catch (_) {}
    });

    // Se ainda há pressão de quota, aparar chaves fz:* não essenciais muito grandes.
    if (removed === 0) {
      keys.forEach((key) => {
        if (!key.startsWith("fz:") || shouldPreserveLocalStorageKey(key)) return;
        try {
          const value = window.localStorage.getItem(key) || "";
          if (value.length > 25000) {
            window.localStorage.removeItem(key);
            removed += 1;
          }
        } catch (_) {}
      });
    }

    try {
      window.dispatchEvent(new CustomEvent("fz:local-storage-quota-recovered", { detail: { reason, removed } }));
    } catch (_) {}
    if (removed > 0) console.warn(`[storage] quota recovery removeu ${removed} chaves locais (${reason}).`);
  } catch (_) {}
  return removed;
}

export function safeLocalStorageSetItem(key, value, options = {}) {
  if (typeof window === "undefined" || !window.localStorage) return false;
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      if (!options?.silent) console.warn(`[storage] falha ao gravar ${key}: ${error?.name || error?.message || "erro"}`);
      return false;
    }
    recoverLocalStorageQuota(`setItem:${key}`);
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (retryError) {
      if (!options?.silent) console.warn(`[storage] quota persistente ao gravar ${key}: ${retryError?.name || retryError?.message || "erro"}`);
      return false;
    }
  }
}

export function safeLocalStorageSetJson(key, value, options = {}) {
  try {
    return safeLocalStorageSetItem(key, JSON.stringify(value), options);
  } catch (_) {
    return false;
  }
}
